/**
 * Racks descriptor-engine save bridge (#997 Story 6.3 Phase 4b; extracted onto
 * the generic `useRepeatableEngineSaveBridge` in Story 6.4).
 *
 * Since Story 6.4 the baseline/session/flush/created-id-map machinery lives in
 * the generic `useRepeatableEngineSaveBridge` (the 27 tests in
 * `use-racks-engine-save-bridge.test.ts` are the refactor oracle — they pass
 * UNCHANGED against this thin wrapper). This file supplies the RACKS bindings
 * (query hook, flush, content-key comparator, invalidation keys, legacy
 * synthesis, editedData key) and renames the generic handle to the unchanged
 * bespoke `RackConfigurationsSaveHandle` contract so every existing call site
 * (leave guard, step-change guard, scope removal, handleSave) keeps working.
 *
 * See `useRepeatableEngineSaveBridge` for the held-baseline / same-chain /
 * reset-to-flush-result semantics (the 6-3 review-patch cluster).
 */

import { useMemo } from 'react';

import {
  rackConfigurationsKeys,
  useRackConfigurations,
} from './queries/useRackConfigurations';
import {
  useRepeatableEngineSaveBridge,
  type AggregateSaveBridgeExtras,
} from './useRepeatableEngineSaveBridge';
import { buildLegacyRackFromRequestFields } from '../components/racks/rackListUtils';
import {
  flushRackEditSession,
  rackEditSessionHasChanges,
} from '../services/rackEditSessionFlush';

import type { RepeatableInstanceSaveHandle } from '../components/repeatableSections/repeatableInstanceSaveHandle';
import type { RackConfigurationsSaveHandle } from '../request/steps/rackpower/subsections/rackConfigurationsSaveHandle';
import type { RackConfiguration } from '../types/rack';

export type UseRacksEngineSaveBridgeParams = {
  requestId: string | undefined;
  isEditing: boolean;
  isOfflineRequest: boolean;
  /** Flat request fields (request + editedData merge) — legacy rack synthesis. */
  legacyRequestFields?: Record<string, unknown>;
  /**
   * The request payload's PERSISTED `rackConfigurations` embed (from `request`,
   * NOT the editedData merge — the merge holds the session). Baseline fallback
   * when the racks query is empty or errored, mirroring the display chain.
   */
  persistedEmbedRacks?: unknown;
  /** Live session list — `editedData.rackConfigurations` (engine bridge writes it). */
  editedRackConfigurations?: unknown;
  /** True when racks were touched this session (`dirtyFields.has('rackConfigurations')`). */
  rackConfigurationsDirty: boolean;
  applyEditedDataPatch?: (patch: Record<string, unknown>) => void;
  restoredBaseline?: RackConfiguration[] | null;
  onDirtyChange?: (dirty: boolean) => void;
};

export function useRacksEngineSaveBridge({
  requestId,
  isEditing,
  isOfflineRequest,
  legacyRequestFields,
  persistedEmbedRacks,
  editedRackConfigurations,
  rackConfigurationsDirty,
  applyEditedDataPatch,
  restoredBaseline,
  onDirtyChange,
}: UseRacksEngineSaveBridgeParams): {
  saveHandle: (RackConfigurationsSaveHandle & AggregateSaveBridgeExtras) | null;
  registerInstanceSaveHandle: (
    handle: RepeatableInstanceSaveHandle | null,
  ) => void;
  /** The held edit-session baseline (for draft-snapshot persistence). */
  sessionBaseline: RackConfiguration[] | null;
} {
  const {
    saveHandle: genericHandle,
    registerInstanceSaveHandle,
    sessionBaseline,
  } = useRepeatableEngineSaveBridge<RackConfiguration>({
    requestId,
    isEditing,
    isOfflineRequest,
    legacyRequestFields,
    persistedEmbedInstances: persistedEmbedRacks,
    editedInstances: editedRackConfigurations,
    instancesDirty: rackConfigurationsDirty,
    applyEditedDataPatch,
    restoredBaseline,
    onDirtyChange,
    editedDataKey: 'rackConfigurations',
    useInstancesQuery: useRackConfigurations,
    hasChanges: rackEditSessionHasChanges,
    flush: flushRackEditSession,
    invalidateListKeys: id => [
      rackConfigurationsKeys.list(id),
      rackConfigurationsKeys.grouped(id),
    ],
    buildLegacyFromRequestFields: buildLegacyRackFromRequestFields,
  });

  const saveHandle = useMemo(():
    | (RackConfigurationsSaveHandle & AggregateSaveBridgeExtras)
    | null => {
    if (!genericHandle) {
      return null;
    }
    return {
      isRackEditorOpen: genericHandle.isEditorOpen,
      hasUnsavedRackEditor: genericHandle.hasUnsavedEditor,
      commitOpenRackEditor: genericHandle.commitOpenEditor,
      saveUnsavedRackIfNeeded: genericHandle.saveUnsavedIfNeeded,
      hasPendingRackChanges: genericHandle.hasPendingChanges,
      flushPendingRackChanges: genericHandle.flushPendingChanges,
      tryDismissRackEditor: genericHandle.tryDismissEditor,
      discardOpenRackEditor: genericHandle.discardOpenEditor,
      // #1501 aggregate save seams (uniform names across all six families)
      getPendingDiff: genericHandle.getPendingDiff,
      absorbExternalFlushResult: genericHandle.absorbExternalFlushResult,
      remapSessionIds: genericHandle.remapSessionIds,
      resetSessionAfterScopeRemoval:
        genericHandle.resetSessionAfterScopeRemoval,
    };
  }, [genericHandle]);

  return { saveHandle, registerInstanceSaveHandle, sessionBaseline };
}
